/**
 * The star chart in three dimensions.
 *
 * His instruction: "For the Knowlege graph of the platform I need it 3D so I can grab it and
 * rotate it, I need the Nodes to be colored coded, the Animation around it need to be more
 * Dynamic".
 *
 * WHY THIS IS A SECOND VIEW RATHER THAN A REWRITE. The 2D graph is not a simple component: it
 * carries local mode with a depth stepper, four persisted force sliders, the timelapse that
 * replays the vault's growth, user-defined colour groups, and the sidebar's compact panel. Porting
 * all of that to three.js would take a long time and would certainly lose some of it quietly. So
 * 3D is its own view on the same data, the toggle is one button, and nothing that works today
 * stops working. Global mode opens in 3D because that is what he asked for; 2D is one press away
 * and is still the only place timelapse lives.
 *
 * WHY IT IS LAZY-LOADED. three.js plus 3d-force-graph is around 700KB, against a service-worker
 * precache of about 3.2MB. This app is an offline-first PWA that he installs on his phone, so
 * making every surface pay that to render a dashboard would be a poor trade. The import happens
 * when the view mounts, which means the deck, the editor and the notes app never carry it.
 *
 * COLOUR CODING IS THE POINT, not decoration. See graph/kinds.ts: the eight MO node kinds take
 * the same eight hues the core nucleus orbits, so purple is a person on both surfaces. 6,361
 * identical dots was throwing away the one fact that makes this vault legible.
 */

import { useEffect, useRef, useState } from 'react';
import { index } from '../worker/client.js';
import { vault } from '../sync/vault.js';
import type { GraphData } from '../worker/api.js';
import { KIND_STYLE, kindOf, readKindColors, type MoKind } from './kinds.js';

/**
 * What the 3d-force-graph instance exposes, narrowed to what this file uses.
 *
 * The constructor is TWO-STEP and getting that wrong is silent: `ForceGraph3D(config)` returns an
 * initialiser, and only calling THAT with the element mounts anything. Passing the element to the
 * first call produces no error and no graph.
 */
interface Fg3dFactory {
  (config?: { controlType?: 'trackball' | 'orbit' | 'fly' }): (el: HTMLElement) => Fg3d;
}

interface Fg3d {
  graphData(d: unknown): Fg3d;
  backgroundColor(c: string): Fg3d;
  nodeColor(fn: (n: never) => string): Fg3d;
  nodeVal(fn: (n: never) => number): Fg3d;
  nodeLabel(fn: (n: never) => string): Fg3d;
  nodeOpacity(v: number): Fg3d;
  nodeResolution(v: number): Fg3d;
  linkColor(fn: (l: never) => string): Fg3d;
  linkOpacity(v: number): Fg3d;
  linkWidth(v: number): Fg3d;
  linkDirectionalParticles(v: number): Fg3d;
  linkDirectionalParticleWidth(v: number): Fg3d;
  linkDirectionalParticleSpeed(v: number): Fg3d;
  onNodeClick(fn: (n: never) => void): Fg3d;
  onNodeHover(fn: (n: never | null) => void): Fg3d;
  width(v: number): Fg3d;
  height(v: number): Fg3d;
  cooldownTicks(v: number): Fg3d;
  warmupTicks(v: number): Fg3d;
  nodeRelSize(v: number): Fg3d;
  d3Force(name: 'charge' | 'link' | 'center'): { strength?: (v: number) => unknown; distance?: (v: number) => unknown } | null;
  d3AlphaDecay(v: number): Fg3d;
  d3VelocityDecay(v: number): Fg3d;
  /**
   * OrbitControls, because that is what `controlType: 'orbit'` builds. The default is
   * TrackballControls, which has NO `autoRotate` property: setting it there would have been a
   * silent no-op, and the "more dynamic" he asked for would simply never have happened. Verified
   * against three-render-objects rather than assumed.
   */
  controls(): { autoRotate: boolean; autoRotateSpeed: number; update: () => void };
  /** Move the camera, optionally looking at a point, over `ms`. Used to fly to a selection. */
  cameraPosition(
    pos: { x: number; y: number; z: number },
    lookAt?: { x: number; y: number; z: number },
    ms?: number,
  ): Fg3d;
  scene(): { add(o: unknown): void; remove(o: unknown): void };
  camera(): unknown;
  renderer(): { setPixelRatio(v: number): void };
  _destructor?: () => void;
}

/**
 * How many nodes the 3D view draws.
 *
 * FILTERING BY DEFAULT, and this was predicted in writing before it was needed. CLAUDE.md, on what
 * degrades as the vault grows: "The force layout draws every node. Past a few thousand it will
 * need clustering or filtering by default rather than a bigger machine." His vault is at 7,133.
 *
 * The reason is DRAW CALLS, not triangles, which is why lowering the sphere resolution did not
 * save it: three-forcegraph builds one mesh per node, so seven thousand nodes is seven thousand
 * draw calls every frame and the tab stops responding entirely. Measured twice on the live page,
 * both times a 45-second timeout with the main thread never returning.
 *
 * So the view draws the most CONNECTED nodes, which is also the more useful half of the answer: a
 * hub with forty links tells him more about the shape of his work than an orphan thread note. The
 * cap is stated on screen and adjustable, never silent, which is the project's own rule about
 * caps and the reason this is a design decision rather than a workaround.
 */
/** What an unselected node fades to. Near the ground but not on it: invisible is not dimmed. */
const DIM = '#39415c';

const CAPS = [600, 1200, 2500, 5000] as const;
const DEFAULT_CAP = 1200;

interface N3 {
  id: string;
  path: string;
  title: string;
  degree: number;
  unresolved?: boolean;
  kind: MoKind;
}

interface Neighbour {
  id: string;
  title: string;
  kind: MoKind;
}

interface Selection {
  id: string;
  title: string;
  path: string;
  kind: MoKind;
  degree: number;
  unresolved: boolean;
  neighbours: Neighbour[];
  /** The opening of the note, or null when there is nothing to read. */
  excerpt: string | null;
}

/**
 * The first few readable lines of a note.
 *
 * Frontmatter is stripped rather than shown: a person node opens with eight lines of YAML, and a
 * preview that is all `first_seen:` and `evidence_count:` tells him nothing he came to find.
 * Wiki-link brackets and heading marks go too, because this is a preview and not an editor.
 */
function excerptOf(id: string): string | null {
  const raw = vault.noteContent(id);
  if (raw === '') return null;
  const body = raw.startsWith('---') ? raw.slice(raw.indexOf('\n---', 3) + 4) : raw;
  const text = body
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\[\[([^\]|]+)(\|[^\]]+)?\]\]/g, '$1')
    .replace(/[*_`>]/g, '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .slice(0, 3)
    .join('\n\n')
    .trim();
  if (text === '') return null;
  return text.length > 700 ? `${text.slice(0, 690).trimEnd()}...` : text;
}

export function Graph3D({
  onOpenNote,
  onExit,
}: {
  onOpenNote: (id: string) => void;
  /** Back to the 2D chart, which still owns timelapse and the force sliders. */
  onExit: () => void;
}): JSX.Element {
  const host = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [counts, setCounts] = useState<Partial<Record<MoKind, number>>>({});
  const [hovered, setHovered] = useState<string | null>(null);
  /** Off while he is dragging, so the graph never fights his hand. */
  const [spinning, setSpinning] = useState(true);
  const [cap, setCap] = useState<number>(DEFAULT_CAP);
  /** What was actually drawn against what exists, so the cap is never silent. */
  const [shown, setShown] = useState<{ drawn: number; total: number }>({ drawn: 0, total: 0 });
  const spinRef = useRef(true);

  /**
   * THE SELECTION.
   *
   * "when I chose a node it should stop and I can see relation and read the content."
   *
   * Three things, and all three were missing: a click opened the note immediately, which threw the
   * chart away; the drift kept turning, so whatever he had just clicked wandered off; and there
   * was nowhere to read what the node actually said.
   *
   * So a click now SELECTS. The drift stops, the camera flies to the node, everything that is not
   * the node or one of its neighbours dims, and a panel names its relations and shows the opening
   * of the note. Opening it is then a deliberate second act, which is the right order: he asked to
   * see the relation and read the content, not to leave.
   */
  const [sel, setSel] = useState<Selection | null>(null);
  /* Refs, because the colour accessors are called by the render loop and must not close over a
     stale render's state. */
  const selRef = useRef<Selection | null>(null);
  const litRef = useRef<Set<string>>(new Set());
  /* Set once the graph exists: re-applies the colour accessors, which is the only public way to
     ask this library for a repaint. */
  const repaintRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    spinRef.current = spinning;
  }, [spinning]);

  useEffect(() => {
    const el = host.current;
    if (el === null) return;
    let fg: Fg3d | null = null;
    let stopped = false;
    let cleanupStars: (() => void) | null = null;
    let ro: ResizeObserver | null = null;

    void (async () => {
      try {
        /*
         * Both imports are dynamic and both are needed here: `three` is used directly for the
         * star field, and importing it in the same tick lets the bundler put them in one chunk
         * rather than two round trips.
         */
        const [{ default: ForceGraph3D }, THREE, data] = await Promise.all([
          import('3d-force-graph'),
          import('three'),
          index.graph() as Promise<GraphData>,
        ]);
        if (stopped) return;

        const colors = readKindColors();
        const all: N3[] = data.nodes.map((n) => ({
          id: n.id,
          path: n.path,
          title: n.title,
          degree: n.degree,
          unresolved: n.unresolved,
          kind: kindOf(n),
        }));

        /*
         * Most connected first, then cut. Sorted on a copy: `data.nodes` is the worker's array and
         * the 2D view reads the same object.
         */
        const nodes =
          all.length <= cap ? all : [...all].sort((a, b) => b.degree - a.degree).slice(0, cap);
        setShown({ drawn: nodes.length, total: all.length });

        // Only links whose BOTH ends survived the cut. A link to a dropped node would either throw
        // or make the library invent a phantom at the origin, which looks like a bug.
        const kept = new Set(nodes.map((n) => n.id));
        const links = data.links
          .filter((l) => kept.has(l.source) && kept.has(l.target))
          .map((l) => ({ ...l }));

        /*
         * The legend counts what is DRAWN, not what exists, and a kind with nothing on screen is
         * absent from it rather than shown as a colour that never appears. Counting the whole vault
         * would put "Correspondence 1,732" beside a view showing sixty of them, which makes the
         * legend a second, quieter lie about the cap.
         */
        const tally: Partial<Record<MoKind, number>> = {};
        for (const n of nodes) tally[n.kind] = (tally[n.kind] ?? 0) + 1;
        setCounts(tally);

        /*
         * `controlType: 'orbit'` is load-bearing twice over: it is the only control type with an
         * `autoRotate`, and its drag-to-orbit is the "grab it and rotate it" he asked for.
         * Trackball's free rotation loses the horizon, so the graph ends up upside down and there
         * is no way back to level.
         */
        /*
         * A neighbour index, built once. Selection has to answer "what is this connected to" on
         * every click, and scanning ten thousand links each time would stutter the drag.
         */
        const byId = new Map(nodes.map((n) => [n.id, n]));
        const adj = new Map<string, Set<string>>();
        for (const l of links) {
          const a = String(l.source);
          const b = String(l.target);
          if (!adj.has(a)) adj.set(a, new Set());
          if (!adj.has(b)) adj.set(b, new Set());
          adj.get(a)!.add(b);
          adj.get(b)!.add(a);
        }

        const g = (ForceGraph3D as unknown as Fg3dFactory)({ controlType: 'orbit' })(el);
        fg = g;

        /*
         * Grabbed BEFORE the chain, not after, because the click handler defined inside it has to
         * be able to stop the drift. Reading it afterwards would leave `controls` undefined at the
         * moment the first click needs it.
         */
        const controls = g.controls();

        /*
         * The colour accessors are named so a click can re-apply them. `nodeColor` is a kapsule
         * prop: setting it again is how the scene is told to repaint, and there is no other public
         * way to ask for one. (Calling a kapsule GETTER per frame is the bug that froze the 2D
         * graph's layout, so this is a set and never a read.)
         */
        const repaint = (): void => {
          g.nodeColor(nodeColour).linkColor(linkColour);
        };
        repaintRef.current = repaint;

        const nodeColour = (n: never): string => {
          const node = n as unknown as N3;
          if (selRef.current === null) return colors[node.kind];
          if (node.id === selRef.current.id) return '#ffffff';
          return litRef.current.has(node.id) ? colors[node.kind] : DIM;
        };
        const endId = (v: unknown): string =>
          typeof v === 'object' && v !== null && 'id' in v ? String((v as { id: string }).id) : String(v);
        const linkColour = (l: never): string => {
          const cur = selRef.current;
          if (cur === null) return colors.attachment;
          const link = l as unknown as { source: unknown; target: unknown };
          return endId(link.source) === cur.id || endId(link.target) === cur.id
            ? colors.person
            : DIM;
        };

        g.graphData({ nodes, links })
          // Transparent, so the page's own ground and the star field below show through and the
          // view works on both themes without a second palette.
          .backgroundColor('rgba(0,0,0,0)')
          .nodeColor(nodeColour)
          .nodeVal((n) => 1 + Math.sqrt((n as unknown as N3).degree))
          .nodeLabel((n) => {
            const node = n as unknown as N3;
            return `${node.title} — ${KIND_STYLE[node.kind].label}`;
          })
          .nodeOpacity(0.88)
          /*
           * PERFORMANCE, ALL THREE OF THESE, and they are not micro-optimisation: the first
           * version froze the tab outright on his real vault. Measured, not guessed: a CDP
           * evaluate against the live page timed out after 45 seconds, which means the main
           * thread never came back. Seven thousand nodes and roughly ten thousand links is a
           * different problem from the demo graphs this library is usually shown with.
           *
           * 1. Node resolution 4, not 8 and certainly not the default 12. This is the sphere
           *    tessellation, and it is per node: at this count it dominates everything else. At
           *    the sizes these render, four segments is indistinguishable from twelve.
           */
          .nodeResolution(4)
          .nodeRelSize(3)
          .linkColor(linkColour)
          .linkOpacity(0.1)
          /*
           * 2. Link width ZERO, which is a behaviour switch and not a thickness. Any width above
           *    zero makes the library build a CYLINDER MESH per link; zero uses a plain line.
           *    Ten thousand cylinders was most of the freeze, and 0.4 looked like a harmless
           *    number.
           */
          .linkWidth(0)
          /*
           * 3. The edge particles are the "more dynamic" he asked for and they are the single
           *    most expensive thing here, because each one is an object that moves every frame.
           *    They stay, but only on a graph small enough to afford them: a local neighbourhood
           *    or a young vault gets traffic, his whole vault does not. The alternative was
           *    keeping them everywhere and shipping a frozen tab, and a feature he cannot open is
           *    not a feature.
           */
          .linkDirectionalParticles(links.length <= 1200 ? 1 : 0)
          .linkDirectionalParticleWidth(0.7)
          .linkDirectionalParticleSpeed(0.004)
          .d3AlphaDecay(0.0228)
          .d3VelocityDecay(0.25)
          /*
           * The simulation has to STOP. Left unbounded it keeps integrating forever behind the
           * idle rotation, so the tab never goes quiet even once the layout has settled.
           * `warmupTicks` runs the first 40 steps with nothing drawn, which is why the graph
           * arrives already shaped instead of exploding outward while he watches.
           */
          .warmupTicks(40)
          .cooldownTicks(220)
          .onNodeClick((n) => {
            const node = n as unknown as N3 & { x?: number; y?: number; z?: number };

            // STOP. This is the first thing he asked for, and it has to happen before the camera
            // moves or the fly-to fights the drift and overshoots.
            controls.autoRotate = false;
            setSpinning(false);

            const near = [...(adj.get(node.id) ?? [])];
            litRef.current = new Set([node.id, ...near]);
            const picked: Selection = {
              id: node.id,
              title: node.title,
              path: node.path,
              kind: node.kind,
              degree: node.degree,
              unresolved: node.unresolved === true,
              /* Most connected first, and capped: a hub with 200 links would push the excerpt off
                 the panel, and the point of the panel is that he can read the note. */
              neighbours: near
                .map((id) => byId.get(id))
                .filter((x): x is N3 => x !== undefined)
                .sort((a, b) => b.degree - a.degree)
                .slice(0, 24)
                .map((x) => ({ id: x.id, title: x.title, kind: x.kind })),
              // A phantom is a name someone wrote in brackets; there is no note to read.
              excerpt: node.unresolved === true ? null : excerptOf(node.id),
            };
            selRef.current = picked;
            setSel(picked);

            // Fly to it, from a little way out along the same direction so the neighbours stay in
            // frame. Looking AT the node rather than at the origin is what makes it read as
            // "this one" instead of "the camera moved".
            const x = node.x ?? 0;
            const y = node.y ?? 0;
            const z = node.z ?? 0;
            const d = Math.hypot(x, y, z) || 1;
            const out = 120;
            g.cameraPosition(
              { x: x + (x / d) * out, y: y + (y / d) * out, z: z + (z / d) * out },
              { x, y, z },
              900,
            );
            repaint();
          })
          .onNodeHover((n) => setHovered(n === null ? null : (n as unknown as N3).title));

        /*
         * SPREAD, or it is a ball. At default forces seven thousand nodes collapse into a solid
         * sphere: every node touches its neighbours, the links are invisible behind them, and the
         * colour coding becomes confetti rather than structure. Stronger repulsion and a longer
         * link distance is what turns the mass back into something with arms and gaps in it.
         */
        const charge = g.d3Force('charge');
        charge?.strength?.(-160);
        const link = g.d3Force('link');
        link?.distance?.(34);

        // Capped at 2: on a 3x phone screen the extra pixels cost more than they show.
        g.renderer().setPixelRatio(Math.min(globalThis.devicePixelRatio || 1, 2));

        /*
         * A drifting sky. Points rather than meshes: 1,400 stars as one draw call, which a
         * per-star mesh would never be. Placed in a shell around the graph rather than a cube, so
         * there is no visible box edge when he rotates past a corner.
         */
        const starGeo = new THREE.BufferGeometry();
        const pos = new Float32Array(1400 * 3);
        for (let i = 0; i < 1400; i += 1) {
          // Deterministic from the index, so the sky is the same on every mount.
          const a = i * 2.399963;
          const b = Math.acos(1 - (2 * (i + 0.5)) / 1400);
          const r = 900 + ((i * 37) % 400);
          pos[i * 3] = Math.sin(b) * Math.cos(a) * r;
          pos[i * 3 + 1] = Math.sin(b) * Math.sin(a) * r;
          pos[i * 3 + 2] = Math.cos(b) * r;
        }
        starGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const stars = new THREE.Points(
          starGeo,
          new THREE.PointsMaterial({ color: 0xffffff, size: 2.4, sizeAttenuation: false, transparent: true, opacity: 0.55 }),
        );
        /*
         * SCENERY MUST NOT BE RAYCASTABLE, and this bug ate the entire selection feature.
         *
         * three-render-objects raycasts the whole scene on every pointer move and takes the first
         * intersection. The star field is a `Points` shell at radius 900-1300 that SURROUNDS the
         * graph, so the ray hits a star before it ever reaches a node, and the library concludes
         * that nothing is under the pointer. The ships do the same at closer range.
         *
         * The symptom was maddeningly specific and I chased it for six clicks: rotate and zoom
         * worked perfectly, because OrbitControls does not raycast, while hover and click never
         * fired ANYWHERE, including on the densest knot in the graph. That combination is the
         * signature of a scene-level raycast being intercepted rather than of anything being
         * mis-aimed.
         *
         * A no-op `raycast` is three.js's own way to say "draw this, never pick it".
         */
        stars.raycast = () => {};
        g.scene().add(stars);

        /*
         * SHIPS, gliding. "The background and Glyding Ships need to be added."
         *
         * Sprites, not meshes: a sprite always faces the camera, which is exactly right here
         * because the plates are drawn as a side view and a rotating quad would show it edge-on
         * and vanish. Three of them, far outside the graph, on straight courses that wrap.
         *
         * They are moved from this file's own animation frame rather than a library hook.
         * `onEngineTick` stops when the simulation cools, which is a second or two in, so ships
         * driven from it would freeze mid-flight and look like a bug. The renderer runs its own
         * loop and reads object positions each frame, so mutating them from outside is enough.
         */
        const plates = ['/ship-freighter.webp', '/ship-fighter.webp', '/ship-interceptor.webp'];
        const loader = new THREE.TextureLoader();
        const ships = plates.map((src, i) => {
          const tex = loader.load(src);
          const sprite = new THREE.Sprite(
            new THREE.SpriteMaterial({
              map: tex,
              transparent: true,
              // Never occlude a node: a ship is scenery and the graph is the content.
              depthWrite: false,
              opacity: 0.5 - i * 0.08,
            }),
          );
          const scale = 190 - i * 55;
          sprite.scale.set(scale, scale * 0.72, 1);
          // Same reason as the stars above: scenery is drawn, never picked.
          sprite.raycast = () => {};
          g.scene().add(sprite);
          return {
            sprite,
            // Deterministic lanes, so the sky is the same on every mount.
            lane: 620 + i * 190,
            y: 240 - i * 210,
            speed: 26 + i * 17,
            /* Alternate directions and stagger the starts, so they are never in formation. */
            dir: i % 2 === 0 ? 1 : -1,
            t: i * 0.37,
          };
        });

        let raf = 0;
        let last = performance.now();
        const glide = (now: number): void => {
          const dt = Math.min(0.05, (now - last) / 1000);
          last = now;
          for (const s2 of ships) {
            s2.t += (dt * s2.speed) / (s2.lane * 2);
            if (s2.t > 1) s2.t -= 1;
            const x = (s2.t * 2 - 1) * s2.lane * s2.dir;
            s2.sprite.position.set(x, s2.y, -s2.lane * 0.6);
            /*
             * Face the way it is going. The plates are drawn nose-LEFT, so a ship travelling in
             * +x has to be mirrored. That sign is the mistake that had every ship on the deck
             * flying backwards until he pointed it out, and it is worth restating rather than
             * leaving as a bare -1.
             */
            s2.sprite.scale.x = Math.abs(s2.sprite.scale.x) * (s2.dir === 1 ? -1 : 1);
          }
          raf = requestAnimationFrame(glide);
        };
        raf = requestAnimationFrame(glide);

        cleanupStars = () => {
          cancelAnimationFrame(raf);
          g.scene().remove(stars);
          starGeo.dispose();
          for (const s2 of ships) {
            g.scene().remove(s2.sprite);
            s2.sprite.material.map?.dispose();
            s2.sprite.material.dispose();
          }
        };

        // Grab and rotate is the orbit controls' own behaviour; this is the idle drift on top of
        // it, and it yields the moment he touches the canvas.
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.45;
        const hold = (): void => {
          controls.autoRotate = false;
        };
        const release = (): void => {
          controls.autoRotate = spinRef.current;
        };
        el.addEventListener('pointerdown', hold);
        globalThis.addEventListener('pointerup', release);

        const size = (): void => {
          g.width(el.clientWidth).height(el.clientHeight);
        };
        size();
        ro = new ResizeObserver(size);
        ro.observe(el);

        setStatus('ready');

        cleanupStars = ((prev) => () => {
          prev?.();
          el.removeEventListener('pointerdown', hold);
          globalThis.removeEventListener('pointerup', release);
        })(cleanupStars);
      } catch {
        // A failed chunk must not leave a blank rectangle with no way out.
        if (!stopped) setStatus('failed');
      }
    })();

    return () => {
      stopped = true;
      ro?.disconnect();
      cleanupStars?.();
      fg?._destructor?.();
      el.innerHTML = '';
    };
  }, [onOpenNote, cap]);

  const toggleSpin = (): void => {
    setSpinning(!spinning);
  };

  /* Letting go of a selection puts the colours back. The refs are cleared first, because the
     repaint reads them. */
  const clearSel = (): void => {
    selRef.current = null;
    litRef.current = new Set();
    setSel(null);
    repaintRef.current?.();
  };

  /* Escape is the reflex for "put that back", and a panel with no keyboard way out is a trap. */
  useEffect(() => {
    if (sel === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') clearSel();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  });

  return (
    <div className="g3-wrap">
      <div className="g3-canvas" ref={host} />

      {status === 'loading' && <p className="g3-note">Loading the star chart...</p>}
      {status === 'failed' && (
        <p className="g3-note">
          The 3D chart could not load.{' '}
          <button type="button" className="g3-link" onClick={onExit}>
            Use the flat chart
          </button>
        </p>
      )}

      {status === 'ready' && (
        <>
          <div className="g3-hud">
            <button type="button" className="g3-btn" onClick={onExit} title="The flat chart, which also has timelapse and the force sliders">
              2D
            </button>
            <button type="button" className="g3-btn" data-on={spinning} onClick={toggleSpin} title="Idle rotation">
              {spinning ? 'Drifting' : 'Held'}
            </button>
            {/* The cap, said out loud. Raising it is his call and the warning is honest: past a
                couple of thousand nodes this is one draw call each and it will slow down. */}
            <select
              className="g3-btn g3-cap"
              value={cap}
              aria-label="How many nodes to draw"
              onChange={(e) => setCap(Number(e.target.value))}
            >
              {CAPS.map((c) => (
                <option key={c} value={c}>
                  {c.toLocaleString()} nodes
                </option>
              ))}
            </select>
          </div>

          {shown.drawn < shown.total && (
            <p className="g3-cap-note">
              Drawing the {shown.drawn.toLocaleString()} most connected of{' '}
              {shown.total.toLocaleString()}
            </p>
          )}

          {/* The legend is the colour coding's other half: a palette nobody can read is a palette
              that means nothing. Only kinds actually present are listed. */}
          <ul className="g3-legend">
            {(Object.keys(KIND_STYLE) as MoKind[])
              .filter((k) => (counts[k] ?? 0) > 0)
              .map((k) => (
                <li key={k}>
                  <span className="g3-swatch" style={{ background: `var(${KIND_STYLE[k].token})` }} />
                  {KIND_STYLE[k].label}
                  <span className="g3-n">{(counts[k] ?? 0).toLocaleString()}</span>
                </li>
              ))}
          </ul>

          {hovered !== null && sel === null && <p className="g3-hover">{hovered}</p>}

          {/*
            THE SELECTION PANEL. Relations first, then the content, then the way out.
            That order is his: "I can see relation and read the content". Opening the note is the
            last thing on the panel rather than the first, because a click used to do it
            immediately and that threw the chart away.
          */}
          {sel !== null && (
            <aside className="g3-sel" aria-label={`${sel.title}, selected`}>
              <header className="g3-sel-head">
                <span className="g3-swatch" style={{ background: `var(${KIND_STYLE[sel.kind].token})` }} />
                <span className="g3-sel-kind">{KIND_STYLE[sel.kind].label}</span>
                <button type="button" className="g3-sel-x" onClick={clearSel} aria-label="Clear selection">
                  &#215;
                </button>
              </header>

              <h2 className="g3-sel-title">{sel.title}</h2>
              <p className="g3-sel-path">{sel.path}</p>

              <p className="g3-sel-count">
                {sel.neighbours.length === 0
                  ? 'Nothing links to this yet'
                  : `${sel.degree} ${sel.degree === 1 ? 'link' : 'links'}`}
                {sel.neighbours.length > 0 && sel.neighbours.length < sel.degree && (
                  <span className="g3-sel-more"> showing {sel.neighbours.length}</span>
                )}
              </p>

              {sel.neighbours.length > 0 && (
                <ul className="g3-sel-links">
                  {sel.neighbours.map((n) => (
                    <li key={n.id}>
                      <span
                        className="g3-swatch"
                        style={{ background: `var(${KIND_STYLE[n.kind].token})` }}
                      />
                      <button type="button" className="g3-sel-link" onClick={() => onOpenNote(n.id)}>
                        {n.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {sel.excerpt !== null ? (
                <div className="g3-sel-read">
                  <p className="g3-sel-label">The note</p>
                  <p className="g3-sel-text">{sel.excerpt}</p>
                </div>
              ) : (
                <p className="g3-sel-none">
                  {sel.unresolved
                    ? 'Named by a link, but not written yet.'
                    : 'Nothing to read here yet.'}
                </p>
              )}

              {!sel.unresolved && (
                <button type="button" className="g3-sel-open" onClick={() => onOpenNote(sel.id)}>
                  Open the note
                </button>
              )}
            </aside>
          )}
        </>
      )}
    </div>
  );
}
